import {
  BankOutlined,
  BarChartOutlined,
  BranchesOutlined,
  FlagOutlined,
  HomeOutlined,
  KeyOutlined,
  PartitionOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  canViewAdminApp,
  checkVisualizeEnabled,
  useCanForFeatures,
  useMe,
  useOrganization,
} from '@salescore/client-common'
import { useNavigate } from '@tanstack/react-router'
import { isNotNullish } from 'effect/Predicate'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

export type LauncherCommandType = 'all' | 'sheet' | 'kpiPivot' | 'page' | 'other' | 'kpi' | 'archived'

export interface LauncherCommandV2 {
  icon: ReactElement
  title: string
  subTitle?: string
  type: LauncherCommandType
  createdBy?: {
    id: string
    name: string
  }
  searchKeyword?: string
  shortcut?: string[]
  method: () => void | Promise<void>
  loading?: boolean
  options?: {
    hideModalAfterExecution?: boolean // default true
  }
}

export const useMoveCommands = (): LauncherCommandV2[] => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const me = useMe()
  const organizationId = useOrganization().id
  const ability = useCanForFeatures()
  const commands: Array<LauncherCommandV2 | undefined> = [
    {
      type: `page` as const,
      icon: <HomeOutlined />,
      title: t(`ホーム画面`),
      searchKeyword: 'home',
      shortcut: ['command', 'h'],
      method: (): void => {
        void navigate({ to: '/o/$organizationId/v2', params: { organizationId } })
      },
    },
    ...(checkVisualizeEnabled(me.myUser) && ability.canManageGoal
      ? [
          {
            type: `page` as const,
            icon: <FlagOutlined />,
            title: t(`目標設定`),
            searchKeyword: 'goal',
            shortcut: ['command', 'g'],
            method: (): void => {
              void navigate({ to: '/o/$organizationId/v2/settings/goal_configs', params: { organizationId } })
            },
          },
          {
            type: `page` as const,
            icon: <PartitionOutlined />,
            title: t(`軸グループ設定`),
            searchKeyword: 'dimension group',
            method: (): void => {
              void navigate({ to: '/o/$organizationId/v2/settings/dimension_groups', params: { organizationId } })
            },
          },
          {
            type: `page` as const,
            icon: <PartitionOutlined />,
            title: t(`目標軸設定`),
            searchKeyword: 'goal dimension',
            method: (): void => {
              void navigate({ to: '/o/$organizationId/v2/settings/goal_dimensions', params: { organizationId } })
            },
          },
        ]
      : []),
    ...(ability.canManageConnection
      ? [
          {
            type: `page` as const,
            icon: <BranchesOutlined />,
            title: t(`連携設定`),
            searchKeyword: 'connection',
            shortcut: ['command', 'c'],
            method: (): void => {
              void navigate({ to: '/o/$organizationId/v2/settings/sources', params: { organizationId } })
            },
          },
        ]
      : []),
    ...(ability.canManageUsers
      ? [
          {
            type: `page` as const,
            icon: <TeamOutlined />,
            title: t(`ユーザー一覧`),
            searchKeyword: 'user',
            shortcut: ['command', 'u'],
            method: (): void => {
              void navigate({ to: '/o/$organizationId/v2/settings/users', params: { organizationId } })
            },
          },
          {
            type: `page` as const,
            icon: <TeamOutlined />,
            title: t(`グループ一覧`),
            searchKeyword: 'group',
            // shortcut: ['command', 'g'],
            method: (): void => {
              void navigate({ to: '/o/$organizationId/v2/settings/user_groups', params: { organizationId } })
            },
          },
          {
            type: `page` as const,
            icon: <BankOutlined />,
            title: t(`組織設定`),
            searchKeyword: 'organization',
            shortcut: ['command', 'o'],
            method: (): void => {
              void navigate({ to: '/o/$organizationId/v2/settings', params: { organizationId } })
            },
          },
        ]
      : []),
    ...(ability.canReadUserActivity
      ? [
          {
            type: `page` as const,
            icon: <BarChartOutlined />,
            title: t(`利用状況`),
            searchKeyword: 'tracking',
            shortcut: ['command', 't'],
            method: (): void => {
              void navigate({ to: '/o/$organizationId/v2/settings/user_activities', params: { organizationId } })
            },
          },
        ]
      : []),
    {
      type: `page` as const,
      icon: <UserOutlined />,
      title: t(`個人設定`),
      searchKeyword: 'me',
      shortcut: ['command', 'm'],
      method: (): void => {
        void navigate({ to: '/o/$organizationId/v2/settings/me', params: { organizationId } })
      },
    },
    // eslint-disable-next-line sonarjs/todo-tag
    canViewAdminApp(me) // TODO: 本来はIdentity.roleにすべき
      ? {
          type: `page`,
          icon: <KeyOutlined />,
          title: '管理者ページ',
          searchKeyword: 'admin',
          shortcut: ['command', 'a'],
          method: (): void => {
            window.open(
              organizationId === ''
                ? process.env.ADMIN_FRONT_URL
                : `${process.env.ADMIN_FRONT_URL}/o/${organizationId}`,
            )
          },
        }
      : undefined,
  ]

  return commands.filter(isNotNullish)
}
